import Select, { type StylesConfig } from '@databases/atlaskit/Select';
import { compactOptionSelectStyleConfig } from '@databases/fields/PopoverEditCommon';
import React, { type ReactNode } from 'react';

export type DefaultValueMode = 'no-default' | 'current' | 'specific';

export type DefaultValueModeSelectOption = {
  label: string;
  shouldRenderPicker: boolean;
  value: DefaultValueMode;
};

export type DefaultValueModeSelectConfig = {
  onChange: (value: DefaultValueMode) => void;
  options: DefaultValueModeSelectOption[];
  value: DefaultValueMode | null;
};

export type DefaultValueModeSelectOptionMessages = {
  current: string;
  noDefault: string;
  specific: string;
};

export const getDefaultValueModeSelectOptions = ({
  current,
  noDefault,
  specific
}: DefaultValueModeSelectOptionMessages): DefaultValueModeSelectOption[] => [
  {
    label: noDefault,
    shouldRenderPicker: false,
    value: 'no-default'
  },
  {
    label: current,
    shouldRenderPicker: false,
    value: 'current'
  },
  {
    label: specific,
    shouldRenderPicker: true,
    value: 'specific'
  }
];

type DefaultValueModeSelectProps = {
  ariaDescribedBy?: string;
  ariaLabel: string;
  inputId: string;
  onChange: (value: DefaultValueMode) => void;
  options: DefaultValueModeSelectOption[];
  placeholder: string;
  value: DefaultValueMode | null;
};

const menuPortalTargetZIndex = 750;

const defaultValueModeSelectStyles: StylesConfig<any, boolean> = {
  ...compactOptionSelectStyleConfig,
  menuPortal: (base) => ({
    ...base,
    zIndex: menuPortalTargetZIndex
  })
};

const DefaultValueModeSelect = ({
  ariaDescribedBy,
  ariaLabel,
  inputId,
  onChange,
  options,
  placeholder,
  value
}: DefaultValueModeSelectProps): ReactNode => {
  const selectedOption = options.find((option) => option.value === value) ?? null;
  const menuPortalTarget =
    typeof globalThis.document === 'undefined' ? undefined : globalThis.document.body;

  return (
    <Select<DefaultValueModeSelectOption, false>
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      inputId={inputId}
      menuPortalTarget={menuPortalTarget}
      menuPosition="fixed"
      options={options}
      placeholder={placeholder}
      spacing="compact"
      styles={defaultValueModeSelectStyles}
      value={selectedOption}
      onChange={(option) => {
        if (option) {
          onChange(option.value);
        }
      }}
    />
  );
};

export default DefaultValueModeSelect;
